import type { AuthSession } from "@mazzager/contracts";

const PRODUCTION_API_URL = "https://mazzager-api.dimasolodovnikov2000.workers.dev/v1";
const PRODUCTION_WS_URL = "wss://mazzager-api.dimasolodovnikov2000.workers.dev/v1/realtime";
const API_URL = import.meta.env.VITE_API_URL || (import.meta.env.PROD ? PRODUCTION_API_URL : "http://localhost:8787/v1");
const STORAGE_KEY = "mazzager.session";

export class ApiClient {
  session: AuthSession | null = null;
  private persistent = true;

  constructor() {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      const temporary = sessionStorage.getItem(STORAGE_KEY);
      this.persistent = Boolean(stored) || !temporary;
      this.session = JSON.parse(stored ?? temporary ?? "null") as AuthSession | null;
    } catch { this.session = null; }
  }

  setSession(session: AuthSession | null, persistent = this.persistent) {
    this.session = session;
    this.persistent = persistent;
    localStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem(STORAGE_KEY);
    if (session) (persistent ? localStorage : sessionStorage).setItem(STORAGE_KEY, JSON.stringify(session));
  }

  async request<T>(path: string, init: RequestInit = {}, retry = true): Promise<T> {
    const headers = new Headers(init.headers);
    if (init.body && !headers.has("content-type")) headers.set("content-type", "application/json");
    if (this.session?.accessToken) headers.set("authorization", `Bearer ${this.session.accessToken}`);
    const response = await fetch(`${API_URL}${path}`, { ...init, headers });
    if (response.status === 401 && retry && this.session?.refreshToken) {
      const refreshed = await fetch(`${API_URL}/auth/refresh`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ refreshToken: this.session.refreshToken }) });
      if (refreshed.ok) {
        this.setSession(await refreshed.json() as AuthSession);
        return this.request<T>(path, init, false);
      }
      this.setSession(null);
    }
    if (!response.ok) {
      const body = await response.json().catch(() => ({ message: response.statusText })) as { message?: string };
      throw new Error(body.message || "Request failed");
    }
    if (response.status === 204) return undefined as T;
    return response.json() as Promise<T>;
  }
}

export const api = new ApiClient();
export const wsUrl = import.meta.env.VITE_WS_URL || (import.meta.env.PROD ? PRODUCTION_WS_URL : "ws://localhost:8787/v1/realtime");
